import {
  LoadingButton,
  LoadingButtonProps,
} from 'src/components/buttons/LoadingButton';
import CurrencyExchangeRoundedIcon from '@mui/icons-material/CurrencyExchangeRounded';
import HowToVoteRoundedIcon from '@mui/icons-material/HowToVoteRounded';
import ShoppingCartRoundedIcon from '@mui/icons-material/ShoppingCartRounded';
import Fade from '@mui/material/Fade';
import { useTheme } from '@mui/material';
import { useMemo } from 'react';
import Typography from '@mui/material/Typography';
import { useRouter } from 'src/hooks/useRouter';

export type ExpandableButtonProps = {
  variant: 'buy' | 'rent' | 'bid' | 'remove';
} & Pick<LoadingButtonProps, 'href'> & {
    expanded: boolean;
    textExpanded: string;
    showAsWhitelisted: boolean;
    price?: string;
  };

export const ExpandableButton = ({
  variant,
  href,
  expanded,
  textExpanded,
  showAsWhitelisted,
  price,
}: ExpandableButtonProps) => {
  const theme = useTheme();
  const router = useRouter();

  const iconProps = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    color: (showAsWhitelisted && variant === 'rent'
      ? 'disabled'
      : undefined) as any,
    sx: { width: 20, height: 20, p: 0 },
  };
  const styles = useMemo(() => {
    switch (variant) {
      case 'buy':
        return {
          background: theme.palette.secondary.vertical,
          boxShadow: theme.shadowsHard.successElevation2,
        };
      case 'bid':
        return {
          background: theme.palette.primary.vertical,
          boxShadow: theme.shadowsHard.primaryElevation2,
        };
      case 'rent':
        return {
          background: !showAsWhitelisted
            ? theme.palette.royal.vertical
            : theme.palette.rarityBackground.disabled,
          boxShadow: !showAsWhitelisted
            ? theme.shadowsHard.royalElevation2
            : undefined,
        };
      case 'remove':
        return {
          background: theme.palette.error.vertical,
          boxShadow: theme.shadowsHard.errorElevation2,
        };
    }
  }, [variant, theme, showAsWhitelisted]);

  return (
    <LoadingButton
      variant="contained"
      disabled={variant === 'rent' && showAsWhitelisted}
      onClick={async () => {
        if (href) {
          await router.push(href);
        }
      }}
      sx={{
        ...styles,
        width: expanded ? '100%' : '25px',
        maxWidth: expanded ? '100%' : '25px',
        padding: expanded ? '16px 8px' : '0px',
        transition: '0.15s ease-out all',
        m: 0,
        height: 40,
        '&:hover': {
          boxShadow: styles.boxShadow,
        },
      }}
    >
      {expanded ? (
        <Fade in={expanded} timeout={{ enter: 200, exit: 200 }}>
          <span
            style={{
              ...theme.lineClamp(1, {
                wrap: false,
                textOverflow: 'ellipsis',
              }),
            }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 'bold', display: 'inline', pr: 1 }}
            >
              {textExpanded}
            </Typography>
            {price}
          </span>
        </Fade>
      ) : (
        <Fade in={!expanded} timeout={{ enter: 200, exit: 200 }}>
          {variant === 'buy' ? (
            <ShoppingCartRoundedIcon {...iconProps} />
          ) : variant === 'bid' ? (
            <HowToVoteRoundedIcon {...iconProps} />
          ) : (
            <CurrencyExchangeRoundedIcon {...iconProps} />
          )}
        </Fade>
      )}
    </LoadingButton>
  );
};
